import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from "react";
import {
  Box,
  Flex,
  Button,
  Text,
  Textarea,
  Select,
  Input,
  SimpleGrid,
  Wrap,
  VStack,
  HStack,
  Checkbox,
  Link,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import ImageUploader from "./ImageUploader";
import InterestEditPill from "./ui/InterestEditPill";
import BirthDatePicker from "./ui/BirthDatePicker";
import {
  CompleteProfile,
  GetAllInterestsByUserId,
  GetUserById,
  UpdateUser,
  UpdateUserLocation,
} from "../services/backend";
import { Trans, useTranslation } from "react-i18next";
import { AuthContext } from "../store/auth-context";
import {
  calculateAge,
  maxBirthDateForMinAge,
  minBirthDateForMaxAge,
} from "../util/calculateAge";
import { detectUserLocation } from "../util/detectCountry";
import { GET_STARTED_BACK_EVENT, MOBILE_BELOW_TOP_BAR_HEIGHT } from "../constants/mobileApp";
import { INTEREST_OPTIONS } from "../constants/interests";
import { withLifestyleDefaults, LIFESTYLE_DEFAULT } from "../util/lifestyleDefaults";
import {
  buildComplianceAttestationPayload,
  clearPendingCompliance,
  readPendingCompliance,
} from "../constants/datingCompliance";

const MIN_BIO_LENGTH = 40;
const MIN_PHOTOS = 2;
const SUGGESTED_INTERESTS = 3;
const MIN_AGE = 18;
const MAX_AGE = 100;
const DRAFT_KEY_PREFIX = "payinapo:get-started-draft:";
export { GET_STARTED_BACK_EVENT };

const availableEducations = [
  "High School",
  "Some College",
  "Associate",
  "Bachelors degree",
  "Masters degree",
  "Doctorate",
  "Trade School",
  "Other",
];

function draftKeyFor(userId) {
  return `${DRAFT_KEY_PREFIX}${userId}`;
}

function readDraft(userId) {
  try {
    const raw = sessionStorage.getItem(draftKeyFor(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeDraft(userId, draft) {
  try {
    sessionStorage.setItem(draftKeyFor(userId), JSON.stringify(draft));
  } catch {
    /* ignore quota */
  }
}

function clearDraft(userId) {
  try {
    sessionStorage.removeItem(draftKeyFor(userId));
  } catch {
    /* ignore */
  }
}

function GetStartedSectionShell({ borderColor, bg, children, ...rest }) {
  return (
    <Box
      p={{ base: 3, md: 5 }}
      borderRadius={{ base: "lg", md: "xl" }}
      borderWidth="1px"
      borderColor={borderColor}
      bg={bg}
      backdropFilter="blur(8px)"
      minW={0}
      maxW="100%"
      overflow="hidden"
      {...rest}
    >
      {children}
    </Box>
  );
}

function GetStartedFieldRow({ label, hint, titleColor, muted, children }) {
  return (
    <Flex direction={{ base: "column", md: "row" }} gap={{ base: 2, md: 6 }} align={{ md: "flex-start" }}>
      <Box flex={{ md: "0 0 220px" }} minW={0}>
        <Text fontSize="sm" fontWeight="semibold" color={titleColor}>
          {label}
        </Text>
        {hint ? (
          <Text fontSize="xs" color={muted} mt={1} lineHeight="tall">
            {hint}
          </Text>
        ) : null}
      </Box>
      <Box flex={1} minW={0} w="100%">
        {children}
      </Box>
    </Flex>
  );
}

function StepProgress({ step, total, titles }) {
  const { t } = useTranslation();
  const titleColor = useColorModeValue("gray.900", "white");
  const muted = useColorModeValue("gray.500", "whiteAlpha.600");
  const idleSeg = useColorModeValue("gray.200", "whiteAlpha.200");
  const doneSeg = useColorModeValue("brand.600", "brand.500");
  const activeSeg = useColorModeValue("brand.500", "brand.300");
  const activeLabel = useColorModeValue("brand.800", "brand.300");

  return (
    <Box w="100%" mt={{ base: 3, md: 4 }}>
      <Flex align="baseline" justify="space-between" gap={3} mb={3}>
        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color={titleColor} noOfLines={1}>
          {titles[step]}
        </Text>
        <Text fontSize="sm" fontWeight="semibold" color={muted} flexShrink={0}>
          {t("getStarted.stepOf", { current: step + 1, total })}
        </Text>
      </Flex>

      <HStack spacing={2} w="100%" mb={2.5} role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={total}>
        {Array.from({ length: total }).map((_, i) => {
          const isDone = i < step;
          const isActive = i === step;
          return (
            <Box
              key={i}
              flex={1}
              h={{ base: "8px", md: "10px" }}
              borderRadius="full"
              bg={isDone ? doneSeg : isActive ? activeSeg : idleSeg}
              boxShadow={isActive ? "0 0 0 3px rgba(163,230,53,0.22)" : "none"}
              transition="background 0.2s ease, box-shadow 0.2s ease"
            />
          );
        })}
      </HStack>

      <HStack spacing={2} w="100%" align="flex-start">
        {titles.map((label, i) => {
          const isDone = i < step;
          const isActive = i === step;
          return (
            <Text
              key={label}
              flex={1}
              fontSize="xs"
              fontWeight={isActive ? "bold" : "medium"}
              color={isActive ? activeLabel : isDone ? titleColor : muted}
              textAlign="center"
              noOfLines={1}
              opacity={isActive || isDone ? 1 : 0.75}
            >
              {label}
            </Text>
          );
        })}
      </HStack>
    </Box>
  );
}

const GetStarted = () => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const toast = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [images, setImages] = useState([]);
  const [bio, setBio] = useState("");
  const [gender, setGender] = useState("");
  const user = authCtx.authenticatedUser ?? (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();
  const userId = user?.id;
  const [isProfileCompleting, setIsProfileCompleting] = useState(false);
  const [complianceAccepted, setComplianceAccepted] = useState(() => {
    const pending = readPendingCompliance();
    return Boolean(pending?.termsAcceptedAt || pending?.ageAttestedAt);
  });
  const [occupation, setOccupation] = useState("");
  const [education, setEducation] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [interestCount, setInterestCount] = useState(0);
  const [selectedInterests, setSelectedInterests] = useState(() => new Set());
  const [lifeStyleData, setLifeStyleData] = useState({
    drinking: LIFESTYLE_DEFAULT,
    smoking: LIFESTYLE_DEFAULT,
    workout: LIFESTYLE_DEFAULT,
    religion: LIFESTYLE_DEFAULT,
  });
  const [hydrated, setHydrated] = useState(false);
  const [showStepErrors, setShowStepErrors] = useState(false);
  const stepScrollRef = useRef(null);

  const maxBirthDate = useMemo(() => maxBirthDateForMinAge(MIN_AGE), []);
  const minBirthDate = useMemo(() => minBirthDateForMaxAge(MAX_AGE), []);

  const bioTrimmed = (bio ?? "").trim();
  const computedAge = birthDate ? calculateAge(birthDate) : null;
  const isAgeValid =
    computedAge !== null && computedAge >= MIN_AGE && computedAge <= MAX_AGE;

  const photosOk = images.length >= MIN_PHOTOS;
  const basicsOk =
    bioTrimmed.length >= MIN_BIO_LENGTH &&
    gender !== "" &&
    birthDate !== "" &&
    isAgeValid;
  const essentialsOk = photosOk && basicsOk;

  const refreshInterestCount = useCallback(async () => {
    if (!userId) return;
    try {
      const interests = await GetAllInterestsByUserId(userId);
      const names = (Array.isArray(interests) ? interests : [])
        .map((i) => (typeof i === "string" ? i : i?.interestName || i?.name))
        .filter(Boolean);
      setSelectedInterests(new Set(names));
      setInterestCount(names.length);
    } catch {
      setInterestCount(0);
    }
  }, [userId]);

  const shellBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const shellBg = useColorModeValue("rgba(255,255,255,0.92)", "rgba(18,18,20,0.88)");
  const shellShadow = useColorModeValue(
    "0 8px 40px rgba(15,23,42,0.08)",
    "0 12px 48px rgba(0,0,0,0.4)"
  );
  const titleColor = useColorModeValue("gray.900", "white");
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const cardHeaderBorder = useColorModeValue("gray.100", "whiteAlpha.100");
  const sectionBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const sectionBg = useColorModeValue("rgba(255,255,255,0.75)", "rgba(255,255,255,0.04)");
  const sectionTint = useColorModeValue("brand.50", "rgba(52,211,153,0.1)");
  const sectionTintYellow = useColorModeValue("yellow.50", "rgba(234,179,8,0.08)");
  const sectionTintBlue = useColorModeValue("blue.50", "rgba(59,130,246,0.1)");
  const inputBg = useColorModeValue("white", "rgba(255,255,255,0.06)");
  const inputColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const inputBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const warnColor = useColorModeValue("orange.600", "orange.300");
  const genderEmptyBorder = useColorModeValue("red.300", "red.400");
  const selectFocus = {
    borderColor: "brand.400",
    boxShadow: "0 0 0 1px var(--chakra-colors-brand-400)",
  };
  const nativeSelectOptionSx = useColorModeValue(
    {},
    {
      "& option, & optgroup": {
        backgroundColor: "#161618",
        color: "rgba(255, 255, 255, 0.92)",
      },
    }
  );

  const fieldStyles = {
    size: "sm",
    borderRadius: "lg",
    color: inputColor,
    bg: inputBg,
    borderColor: inputBorder,
    _focus: selectFocus,
    _placeholder: { color: muted },
    sx: nativeSelectOptionSx,
  };

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;

    (async () => {
      const draft = readDraft(userId);
      if (draft) {
        setBio(draft.bio ?? "");
        setGender(draft.gender ?? "");
        setOccupation(draft.occupation ?? "");
        setEducation(draft.education ?? "");
        setBirthDate(draft.birthDate ?? "");
        setLifeStyleData(
          withLifestyleDefaults({
            drinking: draft.drinking,
            smoking: draft.smoking,
            workout: draft.workout,
            religion: draft.religion,
          })
        );
        if (typeof draft.step === "number" && draft.step >= 0 && draft.step <= 2) {
          setStep(draft.step);
        }
      }

      try {
        const u = await GetUserById(userId);
        if (cancelled || !u) return;

        if (u.isProfileComplete) {
          clearDraft(userId);
          authCtx.markProfileComplete?.(u);
          navigate("/shop", { replace: true });
          return;
        }

        setBio((prev) => prev || u.bio || "");
        setGender((prev) => prev || u.gender || u.preferences?.gender || "");
        setOccupation((prev) => prev || u.occupation || u.preferences?.occupation || "");
        setEducation((prev) => prev || u.education || "");
        const rawBirth = u.birthDate || u.birth_date || "";
        setBirthDate((prev) => prev || (rawBirth ? String(rawBirth).slice(0, 10) : ""));
        setLifeStyleData((prev) =>
          withLifestyleDefaults({
            drinking: prev.drinking || u.drinking || u.preferences?.drinking,
            smoking: prev.smoking || u.smoking || u.preferences?.smoking,
            workout: prev.workout || u.workout || u.preferences?.workout,
            religion: prev.religion || u.religion || u.preferences?.religion,
          })
        );
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();

    refreshInterestCount();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once per user
  }, [userId, refreshInterestCount, navigate]);

  useEffect(() => {
    if (!userId || !hydrated) return;
    writeDraft(userId, {
      step,
      bio,
      gender,
      occupation,
      education,
      birthDate,
      drinking: lifeStyleData.drinking,
      smoking: lifeStyleData.smoking,
      workout: lifeStyleData.workout,
      religion: lifeStyleData.religion,
    });
  }, [userId, hydrated, step, bio, gender, occupation, education, birthDate, lifeStyleData]);

  useEffect(() => {
    if (authCtx.isProfileComplete) {
      navigate("/shop", { replace: true });
    }
  }, [authCtx.isProfileComplete, navigate]);

  useEffect(() => {
    const onBack = () => {
      setShowStepErrors(false);
      setStep((s) => {
        if (s > 0) return s - 1;
        navigate(-1);
        return s;
      });
    };
    window.addEventListener(GET_STARTED_BACK_EVENT, onBack);
    return () => window.removeEventListener(GET_STARTED_BACK_EVENT, onBack);
  }, [navigate]);

  useEffect(() => {
    stepScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [step]);

  const handleLifeStyleChange = (e) => {
    const { name, value } = e.target;
    setLifeStyleData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUploadData = (data) => {
    setImages(data);
  };

  const handleUpdateInterests = () => {
    refreshInterestCount();
  };

  const handleImageUpload = (image) => {
    setImages((prevImages) => [...prevImages, image]);
  };

  const completeProfile = async ({ skipOptional }) => {
    if (!userId) {
      toast({
        title: t("getStarted.profileCompleteError"),
        description: t("getStarted.genericError"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
      return;
    }

    if (!essentialsOk) {
      setShowStepErrors(true);
      toast({
        title: t("getStarted.profileCompleteError"),
        description: t("getStarted.formIncomplete"),
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
      if (!photosOk) setStep(0);
      else if (!basicsOk) setStep(1);
      return;
    }

    const ageToSubmit = calculateAge(birthDate);
    if (ageToSubmit === null || ageToSubmit < MIN_AGE) {
      toast({
        description: t("getStarted.ageInvalid"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
      return;
    }

    if (!complianceAccepted) {
      toast({
        title: t("getStarted.complianceRequired"),
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
      return;
    }

    setIsProfileCompleting(true);

    const lifestyle = withLifestyleDefaults(lifeStyleData);
    const attestation = buildComplianceAttestationPayload({ accepted: true });

    const completePayload = {
      Id: userId,
      Gender: gender,
      Age: ageToSubmit,
      BirthDate: birthDate,
      Bio: bioTrimmed,
      Education: education || undefined,
      Occupation: occupation || undefined,
      Drinking: lifestyle.drinking,
      Smoking: lifestyle.smoking,
      Workout: lifestyle.workout,
      Religion: lifestyle.religion,
      IsProfileComplete: true,
      termsAcceptedAt: attestation.termsAcceptedAt,
      ageAttestedAt: attestation.ageAttestedAt,
      termsVersion: attestation.termsVersion,
    };

    try {
      await CompleteProfile(completePayload);
      await UpdateUser(completePayload);

      const userFromDb = await GetUserById(userId);
      const completedUser = {
        ...(userFromDb || {}),
        id: userId,
        isProfileComplete: true,
        gender,
        occupation,
        education,
        birthDate,
        bio: bioTrimmed,
        drinking: lifestyle.drinking,
        smoking: lifestyle.smoking,
        workout: lifestyle.workout,
        religion: lifestyle.religion,
        age: ageToSubmit,
      };

      clearDraft(userId);
      clearPendingCompliance();
      authCtx.markProfileComplete?.(completedUser);
      await authCtx.authenticateUser(completedUser, { skipStripeSync: true });

      try {
        if (skipOptional) {
          localStorage.setItem("payinapo:finish-profile-banner", "1");
        } else {
          localStorage.removeItem("payinapo:finish-profile-banner");
        }
      } catch {
        /* ignore */
      }

      toast({
        description: skipOptional
          ? t("getStarted.profileCompletedSkip")
          : t("getStarted.profileCompleted"),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "bottom-left",
      });

      navigate("/shop", { replace: true });

      detectUserLocation()
        .then(async (location) => {
          if (!location?.country && location?.lat == null) return;
          try {
            await UpdateUserLocation({
              userId,
              latitude: location.lat,
              longitude: location.long,
              country: location.country || undefined,
            });
            if (location.country) {
              await UpdateUser({ Id: userId, Country: location.country });
            }
          } catch {
            /* ignore */
          }
        })
        .catch(() => {});
    } catch (err) {
      const d = err?.response?.data;
      const serverMsg =
        (typeof d === "string" && d) ||
        d?.message ||
        d?.title ||
        (d?.errors && typeof d.errors === "object"
          ? Object.values(d.errors).flat().filter(Boolean).join(" ")
          : null);
      toast({
        title: t("getStarted.profileCompleteError"),
        description: serverMsg || err?.message || t("getStarted.genericError"),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "bottom-left",
      });
    } finally {
      setIsProfileCompleting(false);
    }
  };

  const goNextFromPhotos = () => {
    if (!photosOk) {
      setShowStepErrors(true);
      toast({
        description: t("getStarted.photosNeeded"),
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom-left",
      });
      return;
    }
    setShowStepErrors(false);
    setStep(1);
  };

  const goNextFromBasics = () => {
    if (!basicsOk) {
      setShowStepErrors(true);
      toast({
        description: t("getStarted.basicsNeeded"),
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom-left",
      });
      return;
    }
    setShowStepErrors(false);
    setStep(2);
  };

  const actionBarBg = useColorModeValue("rgba(255,255,255,0.96)", "rgba(18,18,20,0.96)");
  const actionBarBorder = useColorModeValue("gray.100", "whiteAlpha.100");

  return (
    <Flex
      w="100%"
      maxW="800px"
      mx="auto"
      px={{ base: 0, md: 4 }}
      py={{ base: 0, md: 8 }}
      direction="column"
      minW={0}
      h={{ base: MOBILE_BELOW_TOP_BAR_HEIGHT, md: "auto" }}
      minH={{ base: MOBILE_BELOW_TOP_BAR_HEIGHT, md: "auto" }}
    >
      <Box
        display="flex"
        flexDirection="column"
        flex="1"
        minH={0}
        minW={0}
        borderRadius={{ base: "none", md: "2xl" }}
        borderWidth={{ base: 0, md: "1px" }}
        borderColor={shellBorder}
        bg={shellBg}
        backdropFilter="blur(16px)"
        boxShadow={{ base: "none", md: shellShadow }}
        overflow="hidden"
      >
        <Box
          flexShrink={0}
          px={{ base: 4, md: 6 }}
          pt={{ base: 3, md: 4 }}
          pb={{ base: 3, md: 4 }}
          borderBottomWidth="1px"
          borderColor={cardHeaderBorder}
        >
          <Text
            fontSize="xs"
            fontWeight="semibold"
            letterSpacing="0.06em"
            textTransform="uppercase"
            color={muted}
            noOfLines={1}
          >
            {t("getStarted.title")}
          </Text>
          <Text
            fontSize="sm"
            color={muted}
            mt={1}
            maxW="lg"
            lineHeight="tall"
            noOfLines={2}
            display={{ base: "none", md: "block" }}
          >
            {t("getStarted.subtitle")}
          </Text>
          <StepProgress
            step={step}
            total={3}
            titles={[
              t("getStarted.stepPhotos"),
              t("getStarted.stepBasics"),
              t("getStarted.stepAbout"),
            ]}
          />
        </Box>

        <Box
          ref={stepScrollRef}
          flex="1"
          minH={0}
          overflowY="auto"
          overflowX="hidden"
          px={{ base: 3, md: 6 }}
          py={{ base: 3, md: 6 }}
          sx={{ WebkitOverflowScrolling: "touch" }}
        >
          <VStack spacing={{ base: 3, md: 4 }} align="stretch" pb={{ base: 2, md: 0 }}>
          {step === 0 && (
            <GetStartedSectionShell borderColor={sectionBorder} bg={sectionTintBlue}>
              <Text fontSize="sm" fontWeight="semibold" color={titleColor} mb={1}>
                {t("getStarted.photosHeading")}
              </Text>
              <Text fontSize="xs" color={muted} mb={1} lineHeight="tall">
                {t("getStarted.photosHint")}
              </Text>
              <Text
                fontSize="xs"
                color={photosOk ? muted : showStepErrors ? warnColor : muted}
                mb={3}
                lineHeight="tall"
              >
                {t("getStarted.photosCount", { count: images.length, min: MIN_PHOTOS })}
              </Text>
              <ImageUploader
                onImageUpload={handleImageUpload}
                sendImagesData={handleImageUploadData}
                minPhotos={MIN_PHOTOS}
              />
            </GetStartedSectionShell>
          )}
          {step === 1 && (
            <>
              <GetStartedSectionShell borderColor={sectionBorder} bg={sectionTint}>
                <GetStartedFieldRow
                  label={t("getStarted.aboutYou")}
                  hint={t("getStarted.aboutHint")}
                  titleColor={titleColor}
                  muted={muted}
                >
                  <Textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder={t("getStarted.aboutPlaceholder")}
                    minH={{ base: "88px", md: "110px" }}
                    {...fieldStyles}
                    borderColor={
                      showStepErrors && bioTrimmed.length < MIN_BIO_LENGTH
                        ? genderEmptyBorder
                        : inputBorder
                    }
                  />
                  <Text
                    fontSize="xs"
                    color={bioTrimmed.length >= MIN_BIO_LENGTH ? muted : warnColor}
                    mt={1.5}
                  >
                    {t("getStarted.bioCharCount", {
                      count: bioTrimmed.length,
                      min: MIN_BIO_LENGTH,
                    })}
                  </Text>
                </GetStartedFieldRow>
              </GetStartedSectionShell>

              <GetStartedSectionShell borderColor={sectionBorder} bg={sectionBg}>
                <GetStartedFieldRow
                  label={t("getStarted.gender")}
                  hint={t("getStarted.genderHint")}
                  titleColor={titleColor}
                  muted={muted}
                >
                  <Select
                    placeholder={t("getStarted.genderPlaceholder")}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    {...fieldStyles}
                    borderColor={
                      gender === "" && showStepErrors ? genderEmptyBorder : gender === "" ? genderEmptyBorder : inputBorder
                    }
                  >
                    <option value="Male">{t("getStarted.genderMale")}</option>
                    <option value="Female">{t("getStarted.genderFemale")}</option>
                  </Select>
                  {gender === "" && (
                    <Text fontSize="xs" color={warnColor} mt={1.5}>
                      {t("getStarted.genderRequired")}
                    </Text>
                  )}
                </GetStartedFieldRow>
              </GetStartedSectionShell>

              <GetStartedSectionShell borderColor={sectionBorder} bg={sectionBg}>
                <GetStartedFieldRow
                  label={t("getStarted.dateOfBirth")}
                  hint={t("getStarted.dateOfBirthHint")}
                  titleColor={titleColor}
                  muted={muted}
                >
                  <BirthDatePicker
                    value={birthDate}
                    onChange={setBirthDate}
                    min={minBirthDate}
                    max={maxBirthDate}
                    isInvalid={birthDate !== "" && !isAgeValid}
                  />
                  {birthDate && isAgeValid && (
                    <Text fontSize="xs" color={muted} mt={1.5}>
                      {t("getStarted.ageCalculated", { age: computedAge })}
                    </Text>
                  )}
                  {birthDate && !isAgeValid && (
                    <Text fontSize="xs" color={warnColor} mt={1.5}>
                      {t("getStarted.ageInvalid")}
                    </Text>
                  )}
                </GetStartedFieldRow>
              </GetStartedSectionShell>
            </>
          )}

          {step === 2 && (
            <>
              <Text fontSize="sm" color={muted} lineHeight="tall">
                {t("getStarted.optionalStepHint")}
              </Text>

              <GetStartedSectionShell borderColor={sectionBorder} bg={sectionBg}>
                <GetStartedFieldRow
                  label={t("getStarted.occupation")}
                  hint={t("getStarted.occupationHint")}
                  titleColor={titleColor}
                  muted={muted}
                >
                  <Input
                    name="occupation"
                    value={occupation}
                    onChange={(e) => setOccupation(e.target.value)}
                    placeholder={t("getStarted.occupationPlaceholder")}
                    {...fieldStyles}
                  />
                </GetStartedFieldRow>
              </GetStartedSectionShell>

              <GetStartedSectionShell borderColor={sectionBorder} bg={sectionBg}>
                <GetStartedFieldRow
                  label={t("getStarted.education")}
                  hint={t("getStarted.educationHint")}
                  titleColor={titleColor}
                  muted={muted}
                >
                  <Select
                    placeholder={t("getStarted.educationPlaceholder")}
                    value={education}
                    name="education"
                    onChange={(e) => setEducation(e.target.value)}
                    {...fieldStyles}
                  >
                    {availableEducations.map((edu) => (
                      <option key={edu} value={edu}>
                        {t(`getStarted.educationOptions.${edu}`, { defaultValue: edu })}
                      </option>
                    ))}
                  </Select>
                </GetStartedFieldRow>
              </GetStartedSectionShell>

              <GetStartedSectionShell borderColor={sectionBorder} bg={sectionTintYellow}>
                <Text fontSize="sm" fontWeight="semibold" color={titleColor} mb={1}>
                  {t("getStarted.lifestyleHeading")}
                </Text>
                <Text fontSize="xs" color={muted} mb={4} lineHeight="tall">
                  {t("getStarted.lifestyleHint")}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="xs" fontWeight="medium" color={muted} mb={1.5}>
                      {t("settings.drinking")}
                    </Text>
                    <Select
                      placeholder={t("getStarted.selectPlaceholder")}
                      name="drinking"
                      value={lifeStyleData.drinking}
                      onChange={handleLifeStyleChange}
                      {...fieldStyles}
                    >
                      {["Never", "Occasinally", "Socially", "Regularly", "Prefer not to say"].map((v) => (
                        <option key={v} value={v}>
                          {t(`getStarted.lifestyleOptions.${v}`, { defaultValue: v })}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="medium" color={muted} mb={1.5}>
                      {t("settings.smoking")}
                    </Text>
                    <Select
                      placeholder={t("getStarted.selectPlaceholder")}
                      name="smoking"
                      value={lifeStyleData.smoking}
                      onChange={handleLifeStyleChange}
                      {...fieldStyles}
                    >
                      {[
                        "Non-smoker",
                        "Occasionally",
                        "Social smoker",
                        "Regular smoker",
                        "Trying to quit",
                        "Prefer not to say",
                      ].map((v) => (
                        <option key={v} value={v}>
                          {t(`getStarted.lifestyleOptions.${v}`, { defaultValue: v })}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="medium" color={muted} mb={1.5}>
                      {t("settings.fitness")}
                    </Text>
                    <Select
                      placeholder={t("getStarted.selectPlaceholder")}
                      name="workout"
                      value={lifeStyleData.workout}
                      onChange={handleLifeStyleChange}
                      {...fieldStyles}
                    >
                      {[
                        "Not active",
                        "occasionally",
                        "Active few times week",
                        "Very active",
                        "Fitness lifestyle",
                        "Prefer not to say",
                      ].map((v) => (
                        <option key={v} value={v}>
                          {t(`getStarted.lifestyleOptions.${v}`, { defaultValue: v })}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="medium" color={muted} mb={1.5}>
                      {t("settings.religion")}
                    </Text>
                    <Select
                      placeholder={t("settings.placeholderReligion")}
                      name="religion"
                      value={lifeStyleData.religion}
                      onChange={handleLifeStyleChange}
                      {...fieldStyles}
                    >
                      {[
                        "Christian  Catholic",
                        "Christian  Protestant",
                        "Christian  Other",
                        "Muslim",
                        "Jewish",
                        "Hindu",
                        "Buddhist",
                        "Spiritual but not religious",
                        "Agnostic",
                        "Atheist",
                        "Other",
                        "Prefer not to say",
                      ].map((v) => (
                        <option key={v} value={v}>
                          {t(`getStarted.lifestyleOptions.${v}`, { defaultValue: v })}
                        </option>
                      ))}
                    </Select>
                  </Box>
                </SimpleGrid>
              </GetStartedSectionShell>

              <GetStartedSectionShell borderColor={sectionBorder} bg={sectionTint}>
                <Text fontSize="sm" fontWeight="semibold" color={titleColor} mb={1}>
                  {t("getStarted.interestsHeading")}
                </Text>
                <Text fontSize="xs" color={muted} mb={1} lineHeight="tall">
                  {t("getStarted.interestsHint")}
                </Text>
                <Text fontSize="xs" color={muted} mb={4} lineHeight="tall">
                  {t("getStarted.interestsCountOptional", {
                    count: interestCount,
                    suggested: SUGGESTED_INTERESTS,
                  })}
                </Text>
                <Wrap gap={2} justify="flex-start">
                  {INTEREST_OPTIONS.map((interest) => (
                    <InterestEditPill
                      key={interest}
                      interest={interest}
                      updateState={handleUpdateInterests}
                      initialSelected={selectedInterests.has(interest)}
                    />
                  ))}
                </Wrap>
              </GetStartedSectionShell>
            </>
          )}
          </VStack>
        </Box>

        <Box
          flexShrink={0}
          borderTopWidth="1px"
          borderColor={actionBarBorder}
          bg={actionBarBg}
          backdropFilter="blur(12px)"
          px={{ base: 3, md: 6 }}
          pt={3}
          pb={{ base: "calc(12px + env(safe-area-inset-bottom, 0px))", md: 5 }}
        >
          {step >= 1 ? (
            <Checkbox
              isChecked={complianceAccepted}
              onChange={(e) => setComplianceAccepted(e.target.checked)}
              alignItems="flex-start"
              mb={3}
              colorScheme="brand"
              size="md"
            >
              <Text fontSize="xs" color="gray.600" lineHeight="tall" pl={1} _dark={{ color: "whiteAlpha.700" }}>
                <Trans
                  i18nKey="getStarted.complianceCheckbox"
                  components={{
                    terms: (
                      <Link
                        as={RouterLink}
                        to="/terms#terms"
                        fontWeight="600"
                        textDecoration="underline"
                        textUnderlineOffset="2px"
                      />
                    ),
                    privacy: (
                      <Link
                        as={RouterLink}
                        to="/terms#privacy"
                        fontWeight="600"
                        textDecoration="underline"
                        textUnderlineOffset="2px"
                      />
                    ),
                  }}
                />
              </Text>
            </Checkbox>
          ) : null}
          <Flex
            direction={{ base: "column-reverse", sm: "row" }}
            justify="space-between"
            gap={2}
            align="stretch"
          >
            {step > 0 ? (
              <Button
                variant="ghost"
                borderRadius="full"
                w={{ base: "100%", sm: "auto" }}
                onClick={() => {
                  setShowStepErrors(false);
                  setStep((s) => s - 1);
                }}
              >
                {t("common.back")}
              </Button>
            ) : (
              <Box display={{ base: "none", sm: "block" }} />
            )}

            <Flex
              gap={2}
              justify={{ base: "stretch", sm: "flex-end" }}
              flexWrap="wrap"
              flex={{ base: "1", sm: "unset" }}
              direction={{ base: "column", sm: "row" }}
            >
              {step === 0 && (
                <Button
                  type="button"
                  size="md"
                  px={8}
                  w={{ base: "100%", sm: "auto" }}
                  fontWeight="semibold"
                  borderRadius="full"
                  bgGradient="linear(to-r, brand.300, brand.500)"
                  color="gray.900"
                  onClick={goNextFromPhotos}
                  isDisabled={!photosOk}
                  _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
                >
                  {t("common.next")}
                </Button>
              )}
              {step === 1 && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    borderRadius="full"
                    w={{ base: "100%", sm: "auto" }}
                    isLoading={isProfileCompleting}
                    isDisabled={!basicsOk || !complianceAccepted}
                    onClick={() => completeProfile({ skipOptional: true })}
                  >
                    {t("getStarted.continueToDiscover")}
                  </Button>
                  <Button
                    type="button"
                    size="md"
                    px={8}
                    w={{ base: "100%", sm: "auto" }}
                    fontWeight="semibold"
                    borderRadius="full"
                    bgGradient="linear(to-r, brand.300, brand.500)"
                    color="gray.900"
                    onClick={goNextFromBasics}
                    isDisabled={!basicsOk}
                    _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
                  >
                    {t("common.next")}
                  </Button>
                </>
              )}
              {step === 2 && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    borderRadius="full"
                    w={{ base: "100%", sm: "auto" }}
                    isLoading={isProfileCompleting}
                    isDisabled={!complianceAccepted}
                    onClick={() => completeProfile({ skipOptional: true })}
                  >
                    {t("getStarted.skipForNow")}
                  </Button>
                  <Button
                    type="button"
                    size="md"
                    px={8}
                    w={{ base: "100%", sm: "auto" }}
                    fontWeight="semibold"
                    borderRadius="full"
                    bgGradient="linear(to-r, brand.300, brand.500)"
                    color="gray.900"
                    isLoading={isProfileCompleting}
                    loadingText={t("common.saving")}
                    isDisabled={!complianceAccepted}
                    onClick={() => completeProfile({ skipOptional: false })}
                  >
                    {t("getStarted.continueToDiscover")}
                  </Button>
                </>
              )}
            </Flex>
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
};

export default GetStarted;
